import React, { useState, useEffect } from 'react'
import {
  Upload,
  RefreshCw,
  Plus,
  Trash2,
  Download,
  CheckCircle,
  AlertCircle,
  FileText
} from 'lucide-react'
import * as XLSX from 'xlsx'
import Select from 'react-select'
import Button from '../../components/common/Button'
import Input from '../../components/common/Input'
import Modal from '../../components/common/Modal'
import Card from '../../components/common/Card'
import Table from '../../components/common/Table'
import { useForm } from 'react-hook-form'
import { Controller } from 'react-hook-form'
import toast from 'react-hot-toast'
import { evaluacionService } from '../../services/evaluacionService'
import { detalleNotaService } from '../../services/detalleNotaService'
import { periodoService } from '../../services/periodoService'

const CargarNotas = () => {
  const [evaluaciones, setEvaluaciones] = useState([])
  const [selectedEvaluacion, setSelectedEvaluacion] = useState(null)
  const [notasTemporales, setNotasTemporales] = useState([])
  const [loading, setLoading] = useState(false)
  const [showModal, setShowModal] = useState(false)
  const [modo, setModo] = useState('individual') // 'individual' o 'masivo'
  const [datosFormulario, setDatosFormulario] = useState(null)
  const [grupos, setGrupos] = useState([])
  const [materias, setMaterias] = useState([])
  const [periodos, setPeriodos] = useState([])

  const { register, handleSubmit, reset, watch, control, formState: { errors } } = useForm({
    mode: 'onChange'
  })

  const grupoSelected = watch('grupo_id')
  const periodoSelected = watch('periodo_id')

  // Estilos personalizados para react-select
  const customSelectStyles = {
    control: (base, state) => ({
      ...base,
      borderRadius: '0.75rem',
      borderColor: state.isFocused ? '#3b82f6' : '#e5e7eb',
      backgroundColor: 'transparent',
      padding: '0.5rem 0',
      boxShadow: state.isFocused ? '0 0 0 3px rgba(59, 130, 246, 0.1)' : 'none',
      borderWidth: '1px',
      '&:hover': {
        borderColor: '#3b82f6'
      }
    }),
    option: (base, state) => ({
      ...base,
      backgroundColor: state.isSelected ? '#3b82f6' : state.isFocused ? '#eff6ff' : 'white',
      color: state.isSelected ? 'white' : '#1f2937',
      cursor: 'pointer',
      ':active': {
        backgroundColor: '#3b82f6'
      }
    }),
    menu: (base) => ({
      ...base,
      borderRadius: '0.75rem',
      marginTop: '0.5rem'
    })
  }

  useEffect(() => {
    fetchDatosFormulario()
  }, [])

  const fetchDatosFormulario = async () => {
    try {
      const [evalResponse, periodoResponse] = await Promise.all([
        evaluacionService.getDatosFormulario(),
        periodoService.getPeriodosSimple()
      ])

      if (evalResponse.success) {
        setGrupos(evalResponse.data.grupos || [])
        setMaterias(evalResponse.data.materias || [])
        setDatosFormulario(evalResponse.data)
      }
      if (periodoResponse.success) {
        setPeriodos(periodoResponse.data)
      }
    } catch (error) {
      console.error('Error al cargar datos:', error)
    }
  }

  const fetchEvaluaciones = async (grupoId, periodoId) => {
    if (!grupoId || !periodoId) return

    try {
      setLoading(true)
      const response = await evaluacionService.getEvaluacionesPorGrupoYPeriodo(grupoId, periodoId)

      if (response.success) {
        setEvaluaciones(response.data || [])
        if (response.data?.length === 0) {
          toast.info('No hay evaluaciones para este grupo y período')
        }
      } else {
        toast.error(response.message)
        setEvaluaciones([])
      }
    } catch (error) {
      toast.error('Error al cargar evaluaciones')
      setEvaluaciones([])
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    if (grupoSelected && periodoSelected) {
      fetchEvaluaciones(grupoSelected, periodoSelected)
    }
  }, [grupoSelected, periodoSelected])

  const handleSelectEvaluacion = async (evaluacion) => {
    try {
      setLoading(true)
      const response = await detalleNotaService.getNotasEvaluacion(evaluacion.id)

      if (response.success) {
        const notas = response.data?.estudiantes || []
        setSelectedEvaluacion(evaluacion)
        setNotasTemporales(
          notas.map(nota => ({
            estudiante_id: nota.estudiante_id,
            estudiante_nombre: nota.estudiante_nombre,
            evaluacion_id: evaluacion.id,
            nota: nota.nota || '',
            estado: nota.nota ? 'calificado' : 'sin_calificar'
          }))
        )
        setModo('individual')
        setShowModal(true)
      } else {
        toast.error(response.message)
      }
    } catch (error) {
        console.error('Error al cargar notas:', error)
      toast.error('Error al cargar notas de la evaluación')
    } finally {
      setLoading(false)
    }
  }

  const handleActualizarNota = (idx, nota) => {
    const updated = [...notasTemporales]
    updated[idx].nota = nota
    updated[idx].estado = nota ? 'por_actualizar' : 'sin_calificar'
    setNotasTemporales(updated)
  }

  const handleGuardarNotas = async () => {
    if (!selectedEvaluacion) {
      toast.error('Selecciona una evaluación primero')
      return
    }

    const notasValidas = notasTemporales.filter(n => n.nota && n.nota > 0)

    if (notasValidas.length === 0) {
      toast.error('Debe haber al menos una nota válida')
      return
    }

    try {
      setLoading(true)

      // Validar notas: deben estar entre 0 y 100
      for (const nota of notasValidas) {
        if (nota.nota < 0 || nota.nota > 100) {
          toast.error(`Nota inválida para ${nota.estudiante_nombre}: debe estar entre 0 y 100`)
          return
        }
      }

      const response = await detalleNotaService.createNotasMasivo({
        evaluacion_id: selectedEvaluacion.id,
        notas: notasValidas.map(n => ({
          estudiante_id: n.estudiante_id,
          nota: n.nota
        }))
      })

      if (response.success) {
        toast.success(`${notasValidas.length} nota(s) guardadas exitosamente`)
        setShowModal(false)
        setSelectedEvaluacion(null)
        setNotasTemporales([])
        reset()
      } else {
        toast.error(response.message)
      }
    } catch (error) {
      toast.error('Error al guardar notas')
    } finally {
      setLoading(false)
    }
  }

  const handleDescargarPlantilla = () => {
    if (!grupoSelected || !periodoSelected) {
      toast.error('Selecciona grupo y período')
      return
    }

    try {
      const grupo = grupos.find(g => g.id == grupoSelected)
      const periodo = periodos.find(p => p.id == periodoSelected)

      const wb = XLSX.utils.book_new()

      const safeSheetName = (name) => {
        const cleaned = name.replace(/[\\\/\?\*\[\]]/g, '_').slice(0, 31)
        return cleaned || 'Evaluacion'
      }

      evaluaciones.forEach((evaluacion, index) => {
        if(evaluacion.notas?.length > 0){
          const sheetName = safeSheetName(`${index + 1}-${evaluacion.nombre}` || `Evaluacion ${index + 1}`)

          const rows = (evaluacion.notas?.length > 0 ? evaluacion.notas : [{ estudiante_id: '', estudiante_nombre: '', nota: '' }]).map((nota) => ({
            'Evaluación': evaluacion.nombre,
            // 'Estudiante ID': nota.estudiante_id || '',
            'Estudiante': `${nota?.estudiante?.nombre} ${nota?.estudiante?.apellido}` || 'Nombre del estudiante',
            'Nota': nota?.nota || ''
          }))

          const ws = XLSX.utils.json_to_sheet(rows)
          ws['!cols'] = [
            { wch: 30 }, // Evaluación
            // { wch: 15 }, // Estudiante ID
            { wch: 30 }, // Estudiante
            { wch: 10 }  // Nota
          ]
          XLSX.utils.book_append_sheet(wb, ws, sheetName)
        }
      })

      if (evaluaciones.length === 0) {
        const ws = XLSX.utils.json_to_sheet([
          { 'Evaluación': 'Sin evaluaciones', 'Estudiante': 'Nombre del estudiante', 'Nota': '' }
        ])
        ws['!cols'] = [
          { wch: 30 },
          { wch: 30 },
          { wch: 10 }
        ]
        XLSX.utils.book_append_sheet(wb, ws, 'Notas')
      }

      XLSX.writeFile(wb, `plantilla-notas-${grupo?.codigo || 'grupo'}-${periodo?.nombre || 'periodo'}.xlsx`)
      toast.success('Plantilla descargada correctamente')
    } catch (error) {
      console.error('Error al descargar plantilla:', error)
      toast.error('Error al descargar la plantilla')
    }
  }

  const columns = [
    {
      key: 'nombre',
      label: 'Evaluación',
      render: (row) => (
        <div className="flex items-center space-x-3">
          <div className="h-10 w-10 rounded-xl bg-gradient-to-br from-primary-500 to-accent-500 flex items-center justify-center text-white font-semibold">
            <FileText className="h-5 w-5" />
          </div>
          <div>
            <p className="font-medium text-gray-900 dark:text-gray-100">{row.nombre}</p>
            <p className="text-sm text-gray-600 dark:text-gray-400">{row.porcentaje}%</p>
          </div>
        </div>
      )
    },
    {
      key: 'materia',
      label: 'Materia',
      render: (row) => (
        <span className="text-gray-600 dark:text-gray-400">
          {row.materia?.nombre || 'N/A'}
        </span>
      )
    },
    {
      key: 'actions',
      label: 'Acción',
      render: (row) => (
        <Button
          variant={row.notas.length> 0 ? 'success' : 'primary'}
          size="sm"
          icon={row.notas.length> 0 ?<RefreshCw className="h-4 w-4" /> : <Upload className="h-4 w-4" />}
          onClick={() => handleSelectEvaluacion(row)}
        >
          {row.notas.length> 0 ?'Actualizar Notas' : 'Cargar Notas'}
        </Button>
      )
    }
  ]

  return (
    <div className="p-6 max-w-7xl mx-auto space-y-6">
      {/* Header */}
      <div className="flex items-center space-x-4">
        <div className="w-12 h-12 bg-gradient-to-br from-primary-500 to-accent-500 rounded-2xl flex items-center justify-center shadow-glow">
          <Upload className="h-6 w-6 text-white" />
        </div>
        <div>
          <h1 className="text-3xl font-bold gradient-text">Cargar Notas</h1>
          <p className="text-gray-600 dark:text-gray-400">Carga las notas de evaluaciones por grupo</p>
        </div>
      </div>

      {/* Filtros */}
      <Card className="gradient" shadow="glow-lg">
        <form className="grid grid-cols-1 md:grid-cols-3 gap-4">
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Grupo *
            </label>
            <Controller
              name="grupo_id"
              control={control}
              rules={{ required: 'El grupo es requerido' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={grupos.map(g => ({ value: g.id, label: g.nombre }))}
                  placeholder="Seleccionar grupo"
                  styles={{
                    ...customSelectStyles,
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(option) => field.onChange(option?.value || '')}
                  value={grupos.find(g => g.id == grupoSelected) ? { value: grupoSelected, label: grupos.find(g => g.id == grupoSelected)?.nombre } : null}
                />
              )}
            />
            {errors.grupo_id && <p className="text-error-600 text-sm">{errors.grupo_id.message}</p>}
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Período *
            </label>
            <Controller
              name="periodo_id"
              control={control}
              rules={{ required: 'El período es requerido' }}
              render={({ field }) => (
                <Select
                  {...field}
                  options={periodos.map(p => ({ value: p.id, label: p.nombre }))}
                  placeholder="Seleccionar período"
                  styles={{
                    ...customSelectStyles,
                    menuPortal: base => ({ ...base, zIndex: 9999 })
                  }}
                  isClearable
                  isSearchable
                  menuPortalTarget={document.body}
                  menuPosition="fixed"
                  onChange={(option) => field.onChange(option?.value || '')}
                  value={periodos.find(p => p.id == periodoSelected) ? { value: periodoSelected, label: periodos.find(p => p.id == periodoSelected)?.nombre } : null}
                />
              )}
            />
            {errors.periodo_id && <p className="text-error-600 text-sm">{errors.periodo_id.message}</p>}
          </div>

          <div className="flex items-end">
            <Button
              variant="secondary"
              icon={<Download className="h-4 w-4" />}
              onClick={handleDescargarPlantilla}
              disabled={!grupoSelected || !periodoSelected}
            >
              Descargar Plantilla
            </Button>
          </div>
        </form>
      </Card>

      {/* Tabla de Evaluaciones */}
      <Card className="gradient" shadow="glow-lg">
        <h3 className="text-lg font-bold text-gray-900 dark:text-gray-100 mb-4">
          Evaluaciones Disponibles
        </h3>
        {evaluaciones.length > 0 ? (
          <Table
            columns={columns}
            data={evaluaciones}
            loading={loading}
            emptyMessage="No hay evaluaciones"
            hover
            striped
          />
        ) : (
          <div className="text-center py-8">
            <p className="text-gray-500 dark:text-gray-400">
              Selecciona un grupo y período para ver las evaluaciones
            </p>
          </div>
        )}
      </Card>

      {/* Modal Cargar Notas */}
      <Modal
        isOpen={showModal}
        onClose={() => {
          setShowModal(false)
          setSelectedEvaluacion(null)
          setNotasTemporales([])
        }}
        title={`Cargar Notas - ${selectedEvaluacion?.nombre}`}
        size="xl"
      >
        {selectedEvaluacion && notasTemporales.length > 0 && (
          <div className="space-y-4">
            <div className="max-h-96 overflow-y-auto">
              <table className="w-full">
                <thead className="sticky top-0 bg-gray-100 dark:bg-gray-700">
                  <tr>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Estudiante</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Nota Actual</th>
                    <th className="px-4 py-2 text-left text-sm font-medium text-gray-900 dark:text-gray-100">Nueva Nota</th>
                  </tr>
                </thead>
                <tbody>
                  {notasTemporales.map((nota, idx) => (
                    <tr key={idx} className="border-b border-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-800">
                      <td className="px-4 py-2 text-sm text-gray-900 dark:text-gray-100">
                        {nota.estudiante_nombre}
                      </td>
                      <td className="px-4 py-2 text-sm text-gray-600 dark:text-gray-400">
                        {nota.nota || 'Sin calificar'}
                      </td>
                      <td className="px-4 py-2">
                        <Input
                          type="number"
                          min="0"
                          max="100"
                          step="0.1"
                          value={nota.nota}
                          onChange={(e) => handleActualizarNota(idx, e.target.value ? parseFloat(e.target.value) : '')}
                          placeholder="0.0"
                          className="w-20"
                        />
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
              <Button
                variant="ghost"
                onClick={() => {
                  setShowModal(false)
                  setSelectedEvaluacion(null)
                  setNotasTemporales([])
                }}
              >
                Cancelar
              </Button>
              <Button
                variant="primary"
                icon={<CheckCircle className="h-4 w-4" />}
                onClick={handleGuardarNotas}
                loading={loading}
              >
                Guardar Notas
              </Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  )
}

export default CargarNotas
